import { getInitials } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useSidebarStore } from '@/stores/sidebar.store';
import {
  LogOut,
  Menu,
  Search,
  Settings,
  User,
} from 'lucide-react';
import { memo, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Breadcrumb } from './Breadcrumb';
import { NotificationDropdown } from '../shared/NotificationDropdown';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';
import { ThemeSwitcher } from '../shared/ThemeSwitcher';
import { useSocket } from '@/hooks/useSocket';

export const Topbar = memo(function Topbar() {
  const { user, logout } = useAuthStore();
  const openMobile = useSidebarStore((s) => s.openMobile);
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape key closes user menu
  useEffect(() => {
    if (!showUserMenu) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowUserMenu(false);
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // `liveFeed` is false for a partner session (own login or Login as Partner), which gets no live
  // feed at all — so it shows no connection state, rather than an "Offline"/"Reconnecting…" forever.
  const { enabled: liveFeed, isConnected, onlineUsers } = useSocket();

  return (
    <header
      className="sticky top-0 z-20 flex h-14 items-center justify-between border-b bg-background/80 px-4 sm:px-6 backdrop-blur-sm"
    >
      {/* Left: Hamburger + Breadcrumb.
          `min-w-0` + `flex-1` let the breadcrumb give way on a narrow screen. Without it the
          breadcrumb is sized by its text, and a long one ("Subscribers › <customer name>") pushed
          the whole action group off the right edge — which also stretched the page's layout width,
          so any `position: fixed` dialog rendered wider than the phone screen. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <button
          onClick={openMobile}
          className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0 overflow-hidden">
          <Breadcrumb />
        </div>
      </div>

      {/* Right: Actions — never shrinks; the breadcrumb yields instead. */}
      <div className="flex shrink-0 items-center gap-1">
        {/* Search — triggers CommandPalette via Cmd+K event */}
        <button
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
          className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Search</span>
          <kbd className="pointer-events-none hidden select-none items-center gap-1 rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
            <span className="text-xs">&#8984;</span>K
          </kbd>
        </button>

        {/* Live connection indicator (staff sessions only) */}
        {liveFeed && (
          <div
            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-muted-foreground"
            title={isConnected ? `Live connected (${onlineUsers} online)` : 'Reconnecting...'}
          >
            {isConnected ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                </span>
                <span className="hidden sm:inline text-green-600 dark:text-green-400 font-medium">Live</span>
              </>
            ) : (
              <>
                <span className="h-2 w-2 rounded-full bg-gray-400" />
                <span className="hidden sm:inline">Offline</span>
              </>
            )}
          </div>
        )}

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Notifications */}
        <NotificationDropdown />

        {/* Theme switcher */}
        <ThemeSwitcher />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="ml-1 flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-accent"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {user ? getInitials(`${user.firstName} ${user.lastName}`) : 'U'}
            </div>
            {user && (
              <span className="hidden text-sm font-medium text-foreground sm:inline">
                {user.firstName} {user.lastName}
              </span>
            )}
          </button>

          {showUserMenu && (
            <div role="menu" className="absolute right-0 mt-1 w-56 animate-fade-in rounded-xl border bg-popover p-1 shadow-lg">
              {user && (
                <div className="border-b px-3 py-2.5">
                  <p className="text-sm font-medium text-foreground">{user.firstName} {user.lastName}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              )}
              <div className="py-1">
                <Link
                  to="/settings"
                  onClick={() => setShowUserMenu(false)}
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  Profile
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setShowUserMenu(false)}
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  Settings
                </Link>
              </div>
              <div className="border-t py-1">
                <button
                  onClick={handleLogout}
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-danger transition-colors hover:bg-danger-50 dark:hover:bg-danger/10"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
});
